import React, { useMemo } from 'react';
import BarrasHorizontais from '../shared/BarrasHorizontais';
import { resumoDePrenhez, agruparPorPeriodo, rotuloDePeriodo, taxa } from '../../../data/relatorios';
import { Destaque, Indicador, Cartao, Tabela, Vazio, BotaoPDF, num, pct } from './Pecas';
import { gerarRelatorioPDF, descreverFiltros } from '../../../utils/relatorioPDF';

/**
 * Relatório de prenhez.
 *
 * A métrica que lidera é a TAXA, não a contagem: quantas prenhezes saíram
 * depende de quantas transferências houve, e é a taxa que compara touro com
 * touro e mês com mês.
 *
 * Conferência sem resultado fica de fora do denominador, e aparece contada à
 * parte. Somá-la como "não prenhe" inventaria um fracasso que ninguém
 * constatou; escondê-la deixaria a taxa parecer mais firme do que é.
 */
const PainelPrenhez = ({ linhas, dimensao, granularidade, contexto = {} }) => {
  const dg30 = useMemo(() => linhas.filter(l => l.etapa === 'DG30'), [linhas]);
  const dg60 = useMemo(() => linhas.filter(l => l.etapa === 'DG60'), [linhas]);

  const r30 = useMemo(() => resumoDePrenhez(dg30), [dg30]);
  const r60 = useMemo(() => resumoDePrenhez(dg60), [dg60]);

  const porDimensao = useMemo(() => {
    const mapa = new Map();
    dg30.forEach(l => {
      const chave = l[dimensao] || '—';
      const a = mapa.get(chave) || { chave, conferidas: 0, prenhes: 0 };
      if (l.result) a.conferidas += 1;
      if (l.result === 'PRENHE') a.prenhes += 1;
      mapa.set(chave, a);
    });
    return [...mapa.values()]
      .map(g => ({ ...g, taxa: taxa(g.prenhes, g.conferidas) }))
      .sort((a, b) => b.prenhes - a.prenhes);
  }, [dg30, dimensao]);

  const porPeriodo = useMemo(() => {
    const comFlag = dg30.map(l => ({
      ...l,
      _prenhe: l.result === 'PRENHE' ? 1 : 0,
      _conferida: l.result ? 1 : 0,
    }));
    return agruparPorPeriodo(comFlag, 'session_date', granularidade, ['_prenhe', '_conferida']);
  }, [dg30, granularidade]);

  const COLUNAS = [
    { chave: 'org', rotulo: 'Origem', valor: (l) => l.chave },
    { chave: 'conf', rotulo: 'Conferidas', numerica: true, valor: (l) => num(l.conferidas) },
    { chave: 'pren', rotulo: 'Prenhes', numerica: true, forte: true, valor: (l) => num(l.prenhes) },
    { chave: 'taxa', rotulo: 'Taxa', numerica: true, valor: (l) => pct(l.taxa) },
  ];

  // `r30` e `r60` sao os mesmos objetos dos cartoes. O "sem resultado" vai
  // junto de proposito: e' ele que explica taxa apoiada em poucos casos, e
  // quem le a folha nao tem como perguntar depois.
  const imprimir = () => gerarRelatorioPDF({
    titulo: 'Relatório de prenhez',
    laboratorio: contexto.laboratorio,
    filtros: descreverFiltros(contexto),
    indicadores: [
      { rotulo: 'Taxa DG30', valor: pct(r30.taxa),
        detalhe: `${num(r30.prenhes)} de ${num(r30.conferidas)} conferidas` },
      { rotulo: 'Taxa DG60', valor: pct(r60.taxa),
        detalhe: `${num(r60.prenhes)} de ${num(r60.conferidas)} conferidas` },
      { rotulo: 'Vazias', valor: num(r30.vazias), detalhe: 'no DG30' },
      { rotulo: 'Sem resultado', valor: num(r30.semResultado + r60.semResultado),
        detalhe: 'fora do cálculo da taxa' },
    ],
    detalhe: { titulo: `Por ${contexto.agrupamento || 'origem'} (DG30)`, colunas: COLUNAS, linhas: porDimensao },
  });

  if (linhas.length === 0) {
    return <Vazio mensagem="Nenhuma conferência de prenhez no período selecionado." />;
  }

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Destaque rotulo="Taxa de prenhez DG30" valor={pct(r30.taxa)}
          detalhe={`${num(r30.prenhes)} de ${num(r30.conferidas)} conferidas`} />
        <div className="lg:col-span-2 grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Indicador rotulo="Vazias" valor={num(r30.vazias)} detalhe="no DG30" />
          <Indicador rotulo="Inconclusivas" valor={num(r30.inconclusivas)} detalhe="no DG30" />
          <Indicador rotulo="Taxa DG60" valor={pct(r60.taxa)}
            detalhe={`${num(r60.prenhes)} de ${num(r60.conferidas)}`} />
          {/* Fica visível de propósito: é o que explica uma taxa apoiada em
              poucos casos, e some sozinho quando o lançamento é completado. */}
          <Indicador rotulo="Sem resultado" valor={num(r30.semResultado + r60.semResultado)}
            detalhe="fora do cálculo" />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Cartao titulo={`Prenhezes por ${granularidade === 'mes' ? 'mês' : 'dia'}`}
          subtitulo="DG30, em ordem cronológica.">
          <BarrasHorizontais larguraRotulo="w-16"
            dados={porPeriodo.map(p => ({
              rotulo: rotuloDePeriodo(p.chave), valor: p._prenhe, grupo: p,
            }))}
            detalhe={(d) => `${num(d.grupo._prenhe)} de ${num(d.grupo._conferida)} conferidas · ${pct(taxa(d.grupo._prenhe, d.grupo._conferida))}`}
          />
        </Cartao>

        <Cartao titulo="Prenhezes por origem"
          subtitulo="A taxa ao lado é sobre as conferências daquele grupo.">
          <BarrasHorizontais
            dados={porDimensao.slice(0, 12).map(g => ({
              rotulo: g.chave, valor: g.prenhes, grupo: g,
            }))}
            detalhe={(d) => `${pct(d.grupo.taxa)} de prenhez em ${num(d.grupo.conferidas)} conferidas`}
          />
        </Cartao>
      </div>

      <Tabela
        titulo="Por origem do embrião"
        subtitulo="Taxa calculada só sobre conferências com resultado."
        colunas={COLUNAS}
        linhas={porDimensao}
        acao={<BotaoPDF onClick={imprimir} />}
      />
    </div>
  );
};

export default PainelPrenhez;
